import React, { useEffect, useState, useCallback } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import { adminPickups, adminUpdatePickup } from "../../api";
import { PickupTracker, StatusPill, STAGES } from "./PickupTracker";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmt = (d) => { try { return new Date(d).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); } catch { return ""; } };
const input = "w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#0EA5E9]";
const FINAL = ["credited", "cancelled", "rejected"];
const ACTION = { scheduled: "Schedule & Assign Agent", picked_up: "Mark Picked Up", deposited: "Mark Deposited", credited: "Credit Wallet" };
const FILTERS = ["active", "all", "credited", "cancelled", "rejected"];

const ActionPanel = ({ p, onDone }) => {
  const { toast } = useToast();
  const next = STAGES[STAGES.findIndex((s) => s.key === p.status) + 1]?.key;
  const [f, setF] = useState({ agent_name: "", agent_mobile: "", scheduled_date: p.preferred_date || "", collected_amount: p.amount, note: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (status) => {
    setBusy(true);
    try {
      const body = { status, note: f.note };
      if (status === "scheduled") Object.assign(body, { agent_name: f.agent_name, agent_mobile: f.agent_mobile, scheduled_date: f.scheduled_date });
      if (status === "picked_up") body.collected_amount = parseFloat(f.collected_amount);
      await adminUpdatePickup(p.id, body);
      toast({ title: `Pickup ${p.ref} → ${status.replace("_", " ")}` });
      onDone();
    } catch (err) {
      toast({ title: "Update failed", description: err?.response?.data?.detail?.toString() || "Please try again." });
    } finally { setBusy(false); }
  };

  if (FINAL.includes(p.status)) return null;
  return (
    <div className="mt-4 bg-[#F8FAFC] rounded-xl p-4 space-y-3" data-testid={`admin-pickup-actions-${p.ref}`}>
      {next === "scheduled" && (
        <div className="grid sm:grid-cols-3 gap-2">
          <input placeholder="Agent name" value={f.agent_name} onChange={set("agent_name")} className={input} data-testid={`admin-pickup-agent-name-${p.ref}`} />
          <input placeholder="Agent mobile" value={f.agent_mobile} onChange={set("agent_mobile")} className={input} data-testid={`admin-pickup-agent-mobile-${p.ref}`} />
          <input type="date" value={f.scheduled_date} onChange={set("scheduled_date")} className={input} data-testid={`admin-pickup-date-${p.ref}`} />
        </div>
      )}
      {next === "picked_up" && (
        <input type="number" step="0.01" placeholder="Collected amount" value={f.collected_amount} onChange={set("collected_amount")} className={input} data-testid={`admin-pickup-collected-${p.ref}`} />
      )}
      <input placeholder="Note (optional)" value={f.note} onChange={set("note")} className={input} data-testid={`admin-pickup-note-${p.ref}`} />
      <div className="flex flex-wrap gap-2">
        <button disabled={busy} onClick={() => submit(next)} data-testid={`admin-pickup-advance-${p.ref}`} className="flex items-center gap-2 bg-[#0D1B2A] text-white text-sm font-semibold px-4 py-2 rounded-full disabled:opacity-60">
          {busy && <Loader2 size={14} className="animate-spin" />} {ACTION[next]}
        </button>
        {p.status === "requested" && (
          <button disabled={busy} onClick={() => submit("rejected")} data-testid={`admin-pickup-reject-${p.ref}`} className="text-sm font-semibold px-4 py-2 rounded-full bg-red-50 text-red-600">Reject</button>
        )}
      </div>
    </div>
  );
};

export const AdminCashPickups = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("active");
  const load = useCallback(() => { adminPickups().then(setItems).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const shown = items.filter((p) => filter === "all" || (filter === "active" ? !FINAL.includes(p.status) : p.status === filter));
  return (
    <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm" data-testid="admin-cash-pickups">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((k) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`admin-pickup-filter-${k}`} className={`px-3 py-1.5 rounded-full text-xs font-semibold capitalize ${filter === k ? "bg-[#0D1B2A] text-white" : "bg-[#F0FDFA] text-[#0EA5E9]"}`}>{k}</button>
          ))}
        </div>
        <button onClick={load} data-testid="admin-pickup-refresh" className="flex items-center gap-1.5 text-sm font-semibold text-[#0EA5E9]"><RefreshCw size={14} /> Refresh</button>
      </div>
      <div className="space-y-4">
        {shown.length === 0 && <div className="py-12 text-center text-sm text-[#64748B]" data-testid="admin-pickup-empty">No pickup requests.</div>}
        {shown.map((p) => (
          <div key={p.id} className="border border-slate-100 rounded-2xl p-5" data-testid={`admin-pickup-card-${p.ref}`}>
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
              <div>
                <div className="text-xs text-[#94A3B8] font-semibold">{p.ref} · {fmt(p.created_at)}</div>
                <div className="font-bold text-[#0D1B2A]">{p.business_name || p.retailer_name} <span className="font-normal text-[#64748B]">· {p.contact_name} ({p.contact_mobile})</span></div>
                <div className="text-xs text-[#64748B] mt-0.5">{p.pickup_address} · {p.preferred_date} · {p.preferred_slot}</div>
                {p.remarks && <div className="text-xs text-[#64748B] mt-0.5">“{p.remarks}”</div>}
              </div>
              <div className="text-right">
                <div className="text-lg font-extrabold text-[#0D1B2A]">{inr(p.collected_amount ?? p.amount)}</div>
                <StatusPill status={p.status} testId={`admin-pickup-status-${p.ref}`} />
              </div>
            </div>
            <PickupTracker pickup={p} />
            {p.agent_name && <div className="mt-3 text-xs text-[#1E293B]">Agent: <b>{p.agent_name}</b> · {p.agent_mobile} · {p.scheduled_date}</div>}
            <ActionPanel p={p} onDone={load} />
          </div>
        ))}
      </div>
    </div>
  );
};
